/** Human formatting of entity states, durations and times. */
import type { EntityMeta, EntityState } from '@home/shared';
import { num, str, titleCase, HVAC_LABELS } from './entity.js';

const BINARY: Record<string, [off: string, on: string]> = {
  battery: ['Normal', 'Low'],
  battery_charging: ['Not charging', 'Charging'],
  carbon_monoxide: ['Clear', 'Detected'],
  co: ['Clear', 'Detected'],
  cold: ['Normal', 'Cold'],
  connectivity: ['Disconnected', 'Connected'],
  door: ['Closed', 'Open'],
  garage_door: ['Closed', 'Open'],
  gas: ['Clear', 'Detected'],
  heat: ['Normal', 'Hot'],
  light: ['No light', 'Light detected'],
  lock: ['Locked', 'Unlocked'],
  moisture: ['Dry', 'Wet'],
  motion: ['Clear', 'Motion'],
  moving: ['Stopped', 'Moving'],
  occupancy: ['Clear', 'Occupied'],
  opening: ['Closed', 'Open'],
  plug: ['Unplugged', 'Plugged in'],
  power: ['Off', 'Power'],
  presence: ['Away', 'Home'],
  problem: ['OK', 'Problem'],
  running: ['Not running', 'Running'],
  safety: ['Safe', 'Unsafe'],
  smoke: ['Clear', 'Smoke'],
  sound: ['Quiet', 'Sound'],
  tamper: ['Clear', 'Tampered'],
  update: ['Up to date', 'Update available'],
  vibration: ['Still', 'Vibration'],
  window: ['Closed', 'Open'],
};

export function binaryLabel(deviceClass: string | null | undefined, on: boolean): string {
  const pair = deviceClass ? BINARY[deviceClass] : undefined;
  if (pair) return on ? pair[1] : pair[0];
  return on ? 'On' : 'Off';
}

/** Device classes where "on" is bad news and should be tinted. */
export const ALERT_BINARY = new Set(['moisture', 'smoke', 'gas', 'carbon_monoxide', 'co', 'safety', 'problem', 'tamper', 'heat', 'cold']);

const STATE_LABELS: Record<string, string> = {
  on: 'On',
  off: 'Off',
  open: 'Open',
  closed: 'Closed',
  opening: 'Opening…',
  closing: 'Closing…',
  locked: 'Locked',
  unlocked: 'Unlocked',
  locking: 'Locking…',
  unlocking: 'Unlocking…',
  jammed: 'Jammed',
  open_lock: 'Open',
  playing: 'Playing',
  paused: 'Paused',
  idle: 'Idle',
  standby: 'Standby',
  buffering: 'Buffering',
  home: 'Home',
  not_home: 'Away',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
  disarmed: 'Disarmed',
  armed_home: 'Armed home',
  armed_away: 'Armed away',
  armed_night: 'Armed night',
  armed_vacation: 'Armed vacation',
  armed_custom_bypass: 'Armed (bypass)',
  arming: 'Arming…',
  pending: 'Entry delay',
  triggered: 'Triggered',
  disarming: 'Disarming…',
  cleaning: 'Cleaning',
  docked: 'Docked',
  returning: 'Returning',
  error: 'Error',
  above_horizon: 'Above horizon',
  below_horizon: 'Below horizon',
  'clear-night': 'Clear',
  cloudy: 'Cloudy',
  exceptional: 'Exceptional',
  fog: 'Fog',
  hail: 'Hail',
  lightning: 'Lightning',
  'lightning-rainy': 'Thunderstorms',
  partlycloudy: 'Partly cloudy',
  pouring: 'Heavy rain',
  rainy: 'Rain',
  snowy: 'Snow',
  'snowy-rainy': 'Sleet',
  sunny: 'Sunny',
  windy: 'Windy',
  'windy-variant': 'Windy',
};

export const stateLabel = (s: string): string => STATE_LABELS[s] ?? HVAC_LABELS[s] ?? titleCase(s);

const UNIT_SPACE = new Set(['%', '°C', '°F', '°']);

export function formatNumber(n: number, unit?: string | null, deviceClass?: string | null): string {
  let decimals: number;
  const abs = Math.abs(n);
  if (deviceClass === 'temperature' || unit === '°C' || unit === '°F') decimals = 1;
  else if (unit === '%' || deviceClass === 'humidity' || deviceClass === 'battery') decimals = 0;
  else if (unit === 'W' || unit === 'VA' || unit === 'var') decimals = abs >= 10 ? 0 : 1;
  else if (abs >= 1000) decimals = 0;
  else if (abs >= 100) decimals = abs % 1 === 0 ? 0 : 1;
  else if (abs >= 10) decimals = abs % 1 === 0 ? 0 : 1;
  else decimals = abs % 1 === 0 ? 0 : 2;
  // Large watt readings read better as kW.
  if (unit === 'W' && abs >= 10_000) return `${(n / 1000).toFixed(1)} kW`;
  const v = n.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  if (!unit) return v;
  return UNIT_SPACE.has(unit) ? `${v}${unit}` : `${v} ${unit}`;
}

/** Formats a timestamp state ("2026-10-03T12:00:00+00:00") relative to now. */
export function formatTimestampState(v: string): string {
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return v;
  return relativeTime(t / 1000);
}

/** One-line human state for any entity. */
export function formatState(meta: EntityMeta | undefined, s: EntityState | undefined): string {
  if (!s) return '—';
  if (s.s === 'unavailable') return 'Unavailable';
  if (s.s === 'unknown') return '—';
  const domain = meta?.domain ?? '';
  const dc = meta?.deviceClass ?? str(s.a['device_class']) ?? null;
  const unit = meta?.unit ?? str(s.a['unit_of_measurement']) ?? null;
  switch (domain) {
    case 'binary_sensor':
      return binaryLabel(dc, s.s === 'on');
    case 'sensor':
    case 'number':
    case 'input_number': {
      if (dc === 'timestamp') return formatTimestampState(s.s);
      const n = num(s.s);
      if (n !== undefined) return formatNumber(n * (meta?.scale ?? 1), unit, dc);
      if (dc === 'enum') return titleCase(s.s);
      return s.s;
    }
    case 'light': {
      if (s.s !== 'on') return 'Off';
      const b = num(s.a['brightness']);
      return b !== undefined ? `${Math.max(1, Math.round((b / 255) * 100))}%` : 'On';
    }
    case 'cover': {
      const p = num(s.a['current_position']);
      if (p !== undefined && s.s !== 'opening' && s.s !== 'closing') return p === 0 ? 'Closed' : p === 100 ? 'Open' : `${p}% open`;
      return stateLabel(s.s);
    }
    case 'climate': {
      const action = str(s.a['hvac_action']);
      const t = num(s.a['temperature']);
      const mode = HVAC_LABELS[s.s] ?? titleCase(s.s);
      if (s.s === 'off') return 'Off';
      const bits = [action && action !== 'idle' ? titleCase(action) : mode];
      if (t !== undefined) bits.push(`${t.toFixed(1)}°`);
      return bits.join(' · ');
    }
    case 'media_player': {
      const title = str(s.a['media_title']);
      const artist = str(s.a['media_artist']);
      if ((s.s === 'playing' || s.s === 'paused') && title) return `${artist ? `${artist} – ` : ''}${title}`;
      return stateLabel(s.s);
    }
    case 'fan': {
      if (s.s !== 'on') return 'Off';
      const p = num(s.a['percentage']);
      return p !== undefined ? `${p}%` : 'On';
    }
    case 'scene':
    case 'button':
    case 'input_button':
    case 'event':
      return Number.isFinite(Date.parse(s.s)) ? formatTimestampState(s.s) : stateLabel(s.s);
    case 'input_datetime':
      return s.s;
    case 'sun':
      return stateLabel(s.s);
    case 'update':
      return s.s === 'on' ? `Update ${str(s.a['latest_version']) ?? 'available'}` : 'Up to date';
    case 'water_heater': {
      const t = num(s.a['temperature']);
      return t !== undefined ? `${titleCase(s.s)} · ${t}°` : titleCase(s.s);
    }
    case 'weather': {
      const t = num(s.a['temperature']);
      return `${stateLabel(s.s)}${t !== undefined ? ` · ${t.toFixed(0)}${str(s.a['temperature_unit']) ?? '°'}` : ''}`;
    }
    case 'person':
    case 'device_tracker':
      return s.s === 'home' ? 'Home' : s.s === 'not_home' ? 'Away' : titleCase(s.s);
    default: {
      const n = num(s.s);
      if (n !== undefined && unit) return formatNumber(n, unit, dc);
      return stateLabel(s.s);
    }
  }
}

// ---------------------------------------------------------------- time

export function relativeTime(epochSec: number, now = Date.now()): string {
  const diff = Math.round(now / 1000 - epochSec);
  const abs = Math.abs(diff);
  const fut = diff < 0;
  let v: string;
  if (abs < 45) return fut ? 'in a moment' : 'just now';
  if (abs < 3600) v = `${Math.round(abs / 60)} min`;
  else if (abs < 86400) v = `${Math.round(abs / 3600)} h`;
  else if (abs < 86400 * 14) v = `${Math.round(abs / 86400)} d`;
  else return new Date(epochSec * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return fut ? `in ${v}` : `${v} ago`;
}

export function countdown(secondsLeft: number): string {
  const s = Math.max(0, Math.round(secondsLeft));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const dayTime = new Intl.DateTimeFormat('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const timeOnly = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const dateTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** "Mon 12:00" in the home's timezone. */
export function formatWhen(epochSec: number, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(
      epochSec * 1000,
    );
  } catch {
    return dayTime.format(epochSec * 1000);
  }
}

export const formatTime = (epochSec: number): string => timeOnly.format(epochSec * 1000);
export const formatDateTime = (epochSec: number): string => dateTime.format(epochSec * 1000);

export function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The name the home greeting uses: the first word of the display name, else
 * the username. Lower-case names (the bootstrap owner's display name is the
 * username, "nat") get a capital: "Nat".
 */
export function greetingName(user: { displayName?: string | null; username?: string | null } | null | undefined): string {
  const raw = (user?.displayName?.trim() || user?.username?.trim() || '').split(/\s+/)[0] ?? '';
  if (!raw) return '';
  return raw === raw.toLowerCase() ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
}
